/**
 * ResponsiveDataTable
 *
 * Renders a standard `<Table>` on `md:+` viewports and a tap-friendly card
 * stack on phones. Designed for the list pages (invoices, bills, contacts,
 * journals, transactions) that need to remain usable at 360px width without
 * horizontal scroll.
 *
 * Financial reports (BS, P&L, TB, CF, GL) should NOT use this — those are
 * inherently columnar and belong in an `overflow-x-auto` wrapper, not a card
 * list. Use this when each row represents a single document/record that the
 * user can drill into.
 *
 * Usage:
 *
 *     <ResponsiveDataTable
 *       columns={[
 *         { key: 'number', header: 'Invoice #', render: r => r.number },
 *         { key: 'date',   header: 'Date',      render: r => r.date },
 *         { key: 'amount', header: 'Amount',    render: r => r.amount, className: 'text-right' },
 *       ]}
 *       rows={invoices}
 *       row_key={r => r.id}
 *       mobile_card={(r) => (
 *         <a href={`/invoices/${r.id}`} className="block p-4">
 *           <div className="flex justify-between">
 *             <span className="font-medium">{r.number}</span>
 *             <span>{r.amount}</span>
 *           </div>
 *           <div className="text-xs text-gray-500">{r.date}</div>
 *         </a>
 *       )}
 *     />
 *
 * `renderDesktop` is an escape hatch for adopting this on a screen whose
 * existing desktop table (custom sortable header, a `GlassTable` wrapper, a
 * footer summary row) can't be expressed through `columns` without changing
 * its rendered output — see the prop doc below. Extend this component
 * additively for new needs; never fork it into a local variant.
 */

import * as React from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from '../../lib/cn';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '../table/table';

export interface ResponsiveDataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Cell renderer for the desktop table view. */
  render: (row: T, index: number) => ReactNode;
  /** Optional extra classes for the `<td>`/`<th>` (e.g. `'text-right w-[120px]'`). */
  className?: string;
  /** Optional extra classes for the `<th>` only (overrides `className` on header). */
  headerClassName?: string;
}

interface ResponsiveDataTableBaseProps<T> {
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  /** Mobile (phone) card renderer — one card per row. Required so the row stays usable below 768px. */
  mobileCard: (row: T, index: number) => ReactNode;
  /** Optional row-level className applied to the desktop `<tr>` (e.g. for selection state). Ignored when `renderDesktop` is provided. */
  rowClassName?: (row: T, index: number) => string | undefined;
  /** Optional click handler for the desktop `<tr>`. Mobile cards handle their own taps. Ignored when `renderDesktop` is provided. */
  onRowClick?: (row: T, index: number) => void;
  /** Optional empty-state rendered when `rows.length === 0`. */
  emptyState?: ReactNode;
  /** Optional extra className for the desktop `<Table>` wrapper. Ignored when `renderDesktop` is provided. */
  tableClassName?: string;
  /** Optional extra className for the mobile card list wrapper. */
  mobileListClassName?: string;
  /** Optional `data-testid` on the outermost wrapper. */
  'data-testid'?: string;
}

/**
 * `columns` and `renderDesktop` are mutually exclusive AND jointly
 * exhaustive at the type level (adversarial review, PR #23618): providing
 * both would silently ignore `columns` (renderDesktop always wins at
 * render time), and providing neither would render a headerless table of
 * empty `<td>`s with no compile-time signal either way. The union below
 * makes "exactly one of the two" a type error to violate, not a runtime
 * surprise.
 */
type ResponsiveDataTableProps<T> = ResponsiveDataTableBaseProps<T> &
  (
    | {
        /** Column definitions used to build the generated desktop `<Table>`. */
        columns: ResponsiveDataTableColumn<T>[];
        renderDesktop?: undefined;
      }
    | {
        columns?: undefined;
        /**
         * Escape hatch: fully custom desktop (`md:+`) markup, rendered in
         * place of the generated `<Table>` built from `columns`. Use this to
         * adopt the component on an existing product screen whose desktop
         * table can't be expressed through `columns`/`render` without
         * changing its rendered output — a custom sortable header control, a
         * `GlassTable` wrapper, a footer summary row — so the desktop
         * presentation stays byte-identical. `onRowClick`/`rowClassName`/
         * `tableClassName` are ignored when this is provided; wire
         * equivalent behaviour into the custom markup instead. The fix
         * shape this repo's design-system rules require is "extend the
         * shared component additively", never fork it locally.
         *
         * Receives the same `rows` array passed to the component (not just
         * closed over) so a `renderDesktop` implementation can be written
         * as a pure function of its input rather than relying on the
         * closure capturing the right variable.
         */
        renderDesktop: (rows: T[]) => ReactNode;
      }
  );

function ResponsiveDataTableInner<T>(
  {
    columns,
    rows,
    rowKey,
    mobileCard,
    rowClassName,
    onRowClick,
    emptyState,
    tableClassName,
    mobileListClassName,
    renderDesktop,
    'data-testid': test_id,
  }: ResponsiveDataTableProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  if (rows.length === 0 && emptyState !== undefined) {
    return (
      <div ref={ref} data-testid={test_id}>
        {emptyState}
      </div>
    );
  }

  return (
    <div ref={ref} data-testid={test_id}>
      {/* Mobile: card stack (visible < md). Each row gets its own card. */}
      <ul
        className={cn(
          'divide-y divide-[var(--line)] border border-[var(--line)] glass md:hidden',
          mobileListClassName,
        )}
        data-testid={test_id ? `${test_id}-mobile` : undefined}
      >
        {rows.map((row, index) => (
          <li key={rowKey(row, index)} className="touch-manipulation">
            {mobileCard(row, index)}
          </li>
        ))}
      </ul>

      {/* Desktop: full table (visible md:+). Horizontal scroll wrapper lives inside `<Table>`. */}
      <div
        className={cn('hidden md:block', tableClassName)}
        data-testid={test_id ? `${test_id}-desktop` : undefined}
      >
        {renderDesktop ? (
          renderDesktop(rows)
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                {columns.map((col) => (
                  <TableHeaderCell
                    key={col.key}
                    className={cn(col.headerClassName ?? col.className)}
                  >
                    {col.header}
                  </TableHeaderCell>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={rowKey(row, index)}
                  className={rowClassName?.(row, index)}
                  onClick={
                    onRowClick
                      ? () => {
                          onRowClick(row, index);
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'row' : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e: KeyboardEvent<HTMLTableRowElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(row, index);
                          }
                        }
                      : undefined
                  }
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} className={col.className}>
                      {col.render(row, index)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

/**
 * Generic responsive data table. See {@link ResponsiveDataTableProps}.
 *
 * `forwardRef` erases the generic, so we re-assert the generic call signature
 * via a cast (same pattern as `VirtualList`). This keeps `columns`/`rows`/
 * `row_key` inference per-instance without an `any` leak.
 */
export const ResponsiveDataTable = React.forwardRef(
  ResponsiveDataTableInner,
) as <T>(
  props: ResponsiveDataTableProps<T> & {
    ref?: React.Ref<HTMLDivElement>;
  },
) => React.ReactNode;
